import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { decodeUserFromToken } from 'utils/auth';
import { requestForToken } from 'firebase-config';
import { SessionService } from 'services/SessionsService';
import { message } from 'antd';

export const login = createAsyncThunk(
  'user/login',
  async (formData, thunkAPI) => {
    const { username, password, captcha_token } = formData;
    try {
      const res = await axios.post('token/generate/', {
        username,
        password,
        captcha_token,
        device_type: 0,
      });

      if (res.status === 200) {
        const { access_token, token_type, token, refresh_token } = res.data;
        const user = decodeUserFromToken(access_token ?? token);
        axios.defaults.headers.common.Authorization = `${token_type} ${
          access_token ?? token
        }`;

        localStorage.setItem('token', access_token ?? token);
        localStorage.setItem('refresh_token', refresh_token);
        try {
          const firebaseToken = await requestForToken();
          if (firebaseToken) {
            await axios.post('update-fcm-token-crm/', {
              fcm_token: firebaseToken,
            });
          }
        } catch (error) {
          message.warning('Не удалось подключиться к Firebase');
        }

        return thunkAPI.fulfillWithValue({
          access_token: access_token ?? token,
          token_type,
          refresh_token,
          user,
        });
      }
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response.data.detail);
    }
  }
);

export const sessionClose = createAsyncThunk(
  'user/session-close',
  async (_, thunkAPI) => {
    try {
      const res = SessionService.SessionClose();
      if (res) {
        message.success('Сессия закрыта');
        localStorage.clear();
        axios.defaults.headers.common.Authorization = ``;
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data.detail);
    }
  }
);

export const refreshTokenGen = createAsyncThunk(
  'user/refresh-token',
  async (_, thunkAPI) => {
    try {
      const res = await axios.post('/token/refresh/', {
        refresh_token: localStorage.getItem('refresh_token'),
      });
      if (res.status === 200) {
        const { access_token, token_type } = res.data;
        const user = decodeUserFromToken(access_token);
        axios.defaults.headers.common.Authorization = `${token_type} ${access_token}`;

        localStorage.setItem('token', access_token);

        const firebaseToken = await requestForToken();
        if (firebaseToken) {
          await axios.post('update-fcm-token-crm/', {
            fcm_token: firebaseToken,
          });
        }

        return {
          access_token,
          token_type,
          user,
        };
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

const token = localStorage.getItem('token');
const decodedToken = decodeUserFromToken(token);
const isSuperUser = decodedToken?.isSuperUser;
const tribeRole = decodedToken?.tribeRole;
const tribeId = localStorage.getItem('TribeId');

const initialState = {
  token: token || null,
  tokenType: null,
  isLoggedIn: !!token || (!tribeRole && false),
  isLoading: false,
  error: false,
  user: decodedToken,
  selectedTribe: isSuperUser
    ? null
    : (tribeRole && tribeRole.length > 0 && tribeRole[0].tribe_id) ?? tribeId,
  modal: false,
  refreshToken: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSelectedTribe(state, action) {
      state.selectedTribe = action.payload;
      localStorage.setItem('TribeId', action.payload);
    },
    regenerateTokenNewTribe(state, action) {
      state.token = localStorage.getItem('token');
      state.user = decodeUserFromToken(action.payload);
    },
    openAuthModal(state) {
      state.modal = true;
    },
    closeAuthModal(state) {
      state.modal = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        const { access_token, token_type, user, refresh_token } =
          action.payload;
        state.token = access_token;
        state.tokenType = token_type;
        state.user = user;
        state.isLoggedIn = true;
        state.isLoading = false;
        state.error = false;
        state.modal = false;
        state.refreshToken = refresh_token;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload;
        state.isLoading = false;
        state.modal = true;
      })
      .addCase(sessionClose.fulfilled, (state) => {
        state.token = null;
        state.tokenType = null;
        state.user = null;
        state.isLoggedIn = false;
      })
      .addCase(refreshTokenGen.fulfilled, (state, action) => {
        const { access_token, token_type, user } = action.payload;
        state.token = access_token;
        state.tokenType = token_type;
        state.user = user;
        state.isLoggedIn = true;
        state.isLoading = false;
        state.error = false;
        state.modal = false;
      })
      .addCase(refreshTokenGen.rejected, (state, action) => {
        if (action.payload.status === 400) {
          state.modal = true;
        }
      });
  },
});

export const {
  resetUser,
  setSelectedTribe,
  regenerateTokenNewTribe,
  openAuthModal,
  closeAuthModal,
  refreshToken,
} = authSlice.actions;
export default authSlice.reducer;
